import { useState } from 'react';
import { Calendar, CheckCircle, Circle, Square, CheckSquare, BarChart3, Lock, ChevronDown } from 'lucide-react';
import type { Poll } from '@yawi/types';
import type { UserUnit } from '@yawi/types';
import { useDateFormatter } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { ConfirmChangeVoteModal } from './ConfirmChangeVoteModal';
import { DetailedVotesTable } from './DetailedVotesTable';

interface PollCardProps {
  poll: Poll;
  onVote: (pollId: string, optionIds: string[], unitId?: string) => Promise<void>;
  votingMode?: 'PER_USER' | 'PER_UNIT';
  userUnits?: UserUnit[];
}

export const PollCard: React.FC<PollCardProps> = ({ poll, onVote, votingMode, userUnits }) => {
  const { t } = useI18n();
  const { formatDate } = useDateFormatter();
  const isClosed = poll.status === 'CLOSED';
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [voting, setVoting] = useState(false);
  const [isChangingVote, setIsChangingVote] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Unit selection for PER_UNIT mode
  const ownerUnits = userUnits?.filter((u) => u.isOwner) ?? [];
  const [selectedUnitId, setSelectedUnitId] = useState<string | undefined>(
    ownerUnits.length === 1 ? ownerUnits[0].unitId : undefined
  );
  const isPollPerUnit = votingMode === 'PER_UNIT';

  const totalVotes = poll.options.reduce((sum, opt) => sum + opt.votes, 0);
  const showVotingForm = (!poll.hasVoted || isChangingVote) && !isClosed;

  const handleVote = async () => {
    if (selectedOptions.length === 0) return;
    if (isPollPerUnit && !selectedUnitId) return;
    setVoting(true);
    try {
      await onVote(poll.id, selectedOptions, isPollPerUnit ? selectedUnitId : undefined);
      setIsChangingVote(false);
      setSelectedOptions([]);
    } finally {
      setVoting(false);
    }
  };

  const toggleOption = (optionId: string) => {
    if (poll.allowMultiple) {
      setSelectedOptions((prev) =>
        prev.includes(optionId)
          ? prev.filter((id) => id !== optionId)
          : [...prev, optionId]
      );
    } else {
      setSelectedOptions([optionId]);
    }
  };

  const handleChangeVote = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmChange = () => {
    setShowConfirmModal(false);
    setIsChangingVote(true);
    setSelectedOptions([]);
  };

  const isVoteDisabled = voting || selectedOptions.length === 0 || (isPollPerUnit && !selectedUnitId);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 hover:shadow-md transition-all">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-violet-100 text-violet-600">
          <BarChart3 size={20} />
        </div>
        <h3 className="text-lg font-bold text-slate-800">{poll.title}</h3>
      </div>

      {poll.description && (
        <p className="text-slate-500 text-sm mb-4">{poll.description}</p>
      )}

      {/* Badges */}
      {poll.hasVoted && !isChangingVote && (
        <div className="flex items-center gap-2 text-emerald-600 mb-3">
          <CheckCircle size={16} />
          <span className="text-sm font-medium">{t('polls.voted')}</span>
        </div>
      )}
      {isClosed && !poll.hasVoted && (
        <div className="flex items-center gap-2 text-slate-500 mb-3">
          <Lock size={16} />
          <span className="text-sm font-medium">{t('polls.closed')}</span>
        </div>
      )}

      {/* Results - SIEMPRE se muestran */}
      <div className="space-y-3 mb-4">
        {poll.options.map((option) => {
          const percentage = option.percentage != null
            ? option.percentage
            : totalVotes > 0 ? (option.votes / totalVotes) * 100 : 0;
          const isUserVote = poll.userVotedOptionIds?.includes(option.id);
          return (
            <div key={option.id} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className={`font-medium flex items-center gap-1.5 ${isUserVote ? 'text-violet-700' : 'text-slate-700'}`}>
                  {option.text}
                  {isUserVote && <CheckCircle size={14} className="text-violet-500" />}
                </span>
                <span className={isUserVote ? 'text-violet-600 font-medium' : 'text-slate-500'}>{percentage.toFixed(0)}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${isUserVote ? 'bg-violet-600' : 'bg-violet-400'}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Stats */}
      <div className="flex flex-col gap-1 text-sm text-slate-500 mb-4">
        <span>{totalVotes} {t('polls.totalVotes')}</span>
        {poll.unitsVoted !== undefined && poll.totalUnits !== undefined && poll.totalUnits > 0 && (
          <span className="text-xs text-slate-400">
            {poll.unitsVoted} {t('polls.participation')} {poll.totalUnits} {t('polls.participationUnits')} ({Math.round((poll.unitsVoted / poll.totalUnits) * 100)}%)
          </span>
        )}
      </div>

      {/* Voting Form - Solo si no ha votado o esta cambiando voto */}
      {showVotingForm && (
        <div className="border-t border-slate-100 pt-4 mt-4">
          <p className="text-xs text-slate-400 mb-3">
            {poll.allowMultiple ? t('polls.selectOptions') : t('polls.selectOption')}
          </p>

          {/* Unit Selector for PER_UNIT mode */}
          {isPollPerUnit && ownerUnits.length > 0 && (
            <div className="mb-4">
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                {t('polls.selectUnit')}
              </label>
              {ownerUnits.length === 1 ? (
                <div className="text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                  {t('polls.votingAsUnit')}: <span className="font-medium">{ownerUnits[0].unit.name}</span>
                </div>
              ) : (
                <div className="relative">
                  <select
                    value={selectedUnitId || ''}
                    onChange={(e) => setSelectedUnitId(e.target.value || undefined)}
                    className="w-full appearance-none bg-white border border-slate-200 rounded-lg px-3 py-2 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  >
                    <option value="">{t('polls.selectUnitPlaceholder')}</option>
                    {ownerUnits.map((u) => (
                      <option key={u.unitId} value={u.unitId}>{u.unit.name}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              )}
            </div>
          )}

          {isPollPerUnit && ownerUnits.length === 0 && (
            <div className="mb-4 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
              {t('polls.error.onlyTitulares')}
            </div>
          )}

          <div className="space-y-2 mb-4">
            {poll.options.map((option) => {
              const isSelected = selectedOptions.includes(option.id);
              const Icon = poll.allowMultiple
                ? isSelected ? CheckSquare : Square
                : isSelected ? CheckCircle : Circle;

              return (
                <button
                  key={option.id}
                  onClick={() => toggleOption(option.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    isSelected
                      ? 'border-violet-500 bg-violet-50'
                      : 'border-slate-200 hover:border-violet-300 hover:bg-slate-50'
                  }`}
                >
                  <Icon
                    size={20}
                    className={isSelected ? 'text-violet-600' : 'text-slate-400'}
                  />
                  <span className="text-slate-700 text-sm">{option.text}</span>
                </button>
              );
            })}
          </div>

          <button
            onClick={handleVote}
            disabled={isVoteDisabled}
            className="w-full px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {voting ? t('polls.voting') : t('polls.vote')}
          </button>
        </div>
      )}

      {/* Change Vote Button */}
      {poll.hasVoted && !isClosed && !isChangingVote && (
        <div className="border-t border-slate-100 pt-3 mt-3">
          <button
            onClick={handleChangeVote}
            className="text-sm text-violet-600 hover:text-violet-700 font-medium transition-colors"
          >
            {t('polls.changeVote')}
          </button>
        </div>
      )}

      {/* Detailed Votes for non-anonymous polls */}
      {!poll.isAnonymous && poll.detailedVotes && poll.detailedVotes.length > 0 && (
        <DetailedVotesTable detailedVotes={poll.detailedVotes} />
      )}

      {/* Footer */}
      <div className="flex items-center gap-1 text-xs text-slate-400 mt-4 pt-3 border-t border-slate-100">
        <Calendar size={14} />
        <span>{t('polls.ends')}: {formatDate(poll.endsAt)}</span>
      </div>

      {/* Confirm Modal */}
      {showConfirmModal && (
        <ConfirmChangeVoteModal
          onConfirm={handleConfirmChange}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </div>
  );
};
